import { createContext, memo, useContext, useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux"
import { setIsMenuOpen } from "@/state/menuSlice"
import {
	differentRouteChangeOutsideMenuCurtainAnimation,
	initialCurtainAnimation,
	initialCurtainState,
	isDifferentRouteChangeFromMenuCurtainAnimation,
	loadedCurtainUpAnimation,
} from "../../animations/pageTransitionAnimations"
import { useRouter } from "next/router"
import Curve from "../Curve"
import Loader from "./Loader"
import { useNavigation } from "../../providers/NavigationProvider"
import useMenuCurveSvgPaths from "../../hooks/useMenuCurveSvgPaths"
import { useGSAP } from "@gsap/react"
import { useScrollContext } from "../Scroll"

const CurtainContainer = styled.div`
	position: fixed;
	pointer-events: none;
	inset: 0;
	transform: translateY(0);
	z-index: 30;
	background: white;
	display: flex;
	align-items: center;
	justify-content: center;
`

export const PageTransitionContext = createContext({
	isProjectToProjectOutsideMenuRouteChange: false,
})

const PageTransition = ({ children }) => {
	const router = useRouter()
	const dispatch = useDispatch()
	const { menuTransition, isMenuOpen } = useSelector((state) => state.menu)
	const svgPaths = useMenuCurveSvgPaths()
	const { isLoaded, reset, setIsTransitioning, setIsTransitionCompleted } = useNavigation()
	const scroll = useScrollContext()

	const [isProjectToProjectOutsideMenuRouteChange, setIsProjectToProjectOutsideMenuRouteChange] = useState(false)
	const containerRef = useRef(null)
	const timelineRef = useRef(null)
	const isInitialRender = useRef(true)
	const isNavigationLocked = useRef(true)
	const isNavigatingWithCurtain = useRef(false)
	const previousRoute = useRef(children.key)

	const scrollToTop = scroll.scrollToTop

	const contextValue = useMemo(
		() => ({
			isProjectToProjectOutsideMenuRouteChange,
		}),
		[isProjectToProjectOutsideMenuRouteChange],
	)

	const handleRouteChangeStart = (url, opt) => {
		const { shallow } = opt
		if (!isNavigationLocked.current) return

		const currentPath = router?.asPath || ""
		const isProject = (path) => path.includes("/projects/")
		const isProjectToProject = isProject(currentPath) && isProject(url) && !menuTransition && !isMenuOpen
		if (isProjectToProject) {
			// console.log('isProjecttoProject')
			setIsProjectToProjectOutsideMenuRouteChange(true)
			reset()
			setIsTransitioning(false)
			setIsTransitionCompleted(true)
			scrollToTop()
			return
		}

		const isServicesListing = (path) => path.includes("/services") && !path.includes("/services/")
		const isServiceDetail = (path) => path.includes("/services/")
		const isServiceToSingleService = isServicesListing(currentPath) && isServiceDetail(url) && !menuTransition && !isMenuOpen
		if (isServiceToSingleService) {
			// console.log('isServiceToSingleService')
			reset()
			setIsTransitioning(false)
			setIsTransitionCompleted(true)
			return
		}

		setIsTransitioning(true)

		const animationFn = menuTransition
			? isDifferentRouteChangeFromMenuCurtainAnimation
			: differentRouteChangeOutsideMenuCurtainAnimation

		isNavigatingWithCurtain.current = true

		timelineRef.current = animationFn(
			svgPaths,
			() => {
				isNavigationLocked.current = false
				reset()
				scrollToTop()
				const locale = url.split("/")[1] === "pl" ? "pl" : "en"
				router.push(url, url, { shallow, scroll: !shallow, locale: locale || "en" })
			},
			dispatch,
		)

		timelineRef.current?.restart?.()
		throw "Route change aborted to allow animation."
	}

	const handleRouteChangeComplete = () => {
		if (!isNavigationLocked.current) {
			isNavigationLocked.current = true
		}
		scrollToTop()
	}

	const handleRouteChangeError = () => {
		isNavigationLocked.current = true
		isNavigatingWithCurtain.current = false
		setIsTransitioning(false)
	}

	useEffect(() => {
		if (!router?.events || !svgPaths) return

		router.events.on("routeChangeStart", handleRouteChangeStart)
		router.events.on("routeChangeComplete", handleRouteChangeComplete)
		router.events.on("routeChangeError", handleRouteChangeError)

		return () => {
			router.events.off("routeChangeStart", handleRouteChangeStart)
			router.events.off("routeChangeComplete", handleRouteChangeComplete)
			router.events.off("routeChangeError", handleRouteChangeError)
		}
	}, [router, menuTransition, svgPaths])

	useGSAP(
		() => {
			if (!svgPaths) return

			// Initial Render Animation
			if (isInitialRender.current) {
				// console.log('isInitialRender.current')
				if (isLoaded) {
					setIsTransitioning(true)
					timelineRef.current = initialCurtainAnimation(svgPaths, () => {
						setIsTransitionCompleted(true)
						setIsTransitioning(false)
					})
				} else {
					timelineRef.current = initialCurtainState(svgPaths, () => {
						setIsTransitionCompleted(false)
					})
				}
				isInitialRender.current = false
				return timelineRef.current.restart()
			}

			if (!isLoaded) return

			const isSameRoute = children.key === previousRoute.current
			const isDifferentRouteFromMenu = !isSameRoute && menuTransition
			const isDifferentRouteOutsideMenu = !isSameRoute && !menuTransition
			const isProjectToProject =
				!isSameRoute &&
				children.key.includes("/projects/") &&
				previousRoute.current?.includes("/projects/") &&
				!menuTransition &&
				!isNavigatingWithCurtain.current
			const isServiceToSingleService =
				!isSameRoute &&
				children.key.includes("/services/") &&
				previousRoute.current?.includes("/services") &&
				!previousRoute.current?.includes("/services/") &&
				!menuTransition &&
				!isNavigatingWithCurtain.current

			setIsProjectToProjectOutsideMenuRouteChange(isProjectToProject)
			previousRoute.current = children.key

			if (isProjectToProject || isServiceToSingleService) {
				// console.log(isProjectToProject ? 'isProjectToProject' : 'isServiceToSingleService')
				setIsTransitionCompleted(true)
				scrollToTop()
				return
			}

			if (isNavigatingWithCurtain.current && (isDifferentRouteFromMenu || isDifferentRouteOutsideMenu)) {
				// console.log('Handle post-navigation curtain state')
				dispatch(setIsMenuOpen(false))
				setIsTransitionCompleted(false)
				scrollToTop()
				isNavigatingWithCurtain.current = false

				if (isLoaded) {
					setIsTransitioning(true)
					timelineRef.current = loadedCurtainUpAnimation(svgPaths, () => {
						setIsTransitionCompleted(true)
						scrollToTop()
						setIsTransitioning(false)
					})
					return timelineRef.current.restart()
				}

				setIsTransitioning(false)
				return
			}

			if (isLoaded) {
				// console.log('Loaded Curtain Up Animation')
				setIsTransitioning(true)
				timelineRef.current = loadedCurtainUpAnimation(svgPaths, () => {
					setIsTransitionCompleted(true)
					scrollToTop()
					setIsTransitioning(false)
				})
				return timelineRef.current.restart()
			}
		},
		{ scope: containerRef, dependencies: [children, isLoaded] },
	)

	return (
		<PageTransitionContext.Provider value={contextValue}>
			<div ref={containerRef}>
				<CurtainContainer className="curtain-page-transition-block">
					<Loader />
					<Curve variant="open" svgPaths={svgPaths} id="transition" />
					<Curve variant="close" svgPaths={svgPaths} id="transition" />
				</CurtainContainer>
				{children}
			</div>
		</PageTransitionContext.Provider>
	)
}

export function usePageTransitionContext(selector) {
	const context = useContext(PageTransitionContext)
	if (typeof context === "undefined") {
		throw new Error("usePageTransitionContext must be used within a PageTransitionContext.Provider")
	}
	return typeof selector === "function" ? selector(context) : context
}

export default memo(PageTransition)
